// Add Variation dialog. Owns local form state + validation; delegates
// rendering to VariationFormFields. Mirrors the SowFormModal pattern.
// The Edit flow lives inline (VariationInlineExpand), not in this modal
// — `mode === "edit"` is supported here for symmetry but the primary
// path is create.

import { useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import VariationFormFields from "./VariationFormFields";
import {
  buildEmptyVariationFormState,
  parseValueImpact,
  stateFromVariation,
  toCreateVariationInput,
  toUpdateVariationInput,
  type VariationFieldErrors,
  type VariationFormState,
} from "./variationFormState";
import { validateAllocations } from "./variationTargets";
import { nextTrackingNumber } from "./nextTrackingNumber";
import { isValidIsoDate } from "../shared/isoDateValidation";
import useSeededFormState from "../../../../hooks/useSeededFormState";
import type {
  CreateVariationInput,
  Sow,
  UpdateVariationInput,
  Variation,
} from "../../../../types";

export type VariationFormMode = "create" | "edit";

interface VariationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: VariationFormMode;
  contractId: string;
  source?: Variation;
  gstApplies: boolean | undefined;
  /** SOWs available for the Varies dropdown when the user picks Specific SOW. */
  sows: Sow[];
  /** Existing variations on the contract — used to compute the canonical
   *  next number client-side so the user sees the allocated form before
   *  save. Display hint only; backend allocates atomically when
   *  numberOverrideMode is false. */
  existingVariations?: readonly Variation[];
  /** Number format from the contract's `variationNumberFormat` setting.
   *  When `"nn"` the seeded preview is `01`, `02`, ...; when
   *  `"var-yyyy-nnn"` it is `VAR-2026-001`, etc. Defaults to `"nn"` to
   *  mirror the per-contract default. */
  numberFormat?: "nn" | "var-yyyy-nnn";
  onCreate?: (input: CreateVariationInput) => Promise<unknown>;
  onUpdate?: (input: UpdateVariationInput) => Promise<unknown>;
  validateUniqueNumber?: (next: string) => Promise<string | null>;
  isSubmitting?: boolean;
}

function validate(
  state: VariationFormState,
  numberFormat: "nn" | "var-yyyy-nnn",
): VariationFieldErrors {
  const errors: VariationFieldErrors = {};
  if (!state.name.trim()) errors.name = "Name is required";
  // Effective date is optional on Proposed variations — the backend only
  // requires it at /approve time. Reject only a non-empty value that
  // fails ISO-date parsing; an empty string is acceptable on submit.
  if (
    state.effectiveDate.trim().length > 0 &&
    !isValidIsoDate(state.effectiveDate)
  ) {
    errors.effectiveDate = "Enter a valid effective date";
  }
  let parsedValue: number | null = null;
  if (!state.nonFinancial) {
    parsedValue = parseValueImpact(state.valueImpact);
    if (parsedValue === null) {
      errors.valueImpact =
        "Enter a value, or tick Non-financial variation";
    } else if (parsedValue === 0) {
      errors.valueImpact =
        "Value cannot be zero. Use Non-financial for variations with no monetary effect.";
    }
  }
  const allocationCheck = validateAllocations(
    state.targets,
    state.nonFinancial ? null : (parsedValue ?? 0),
  );
  if (allocationCheck) errors.targets = allocationCheck;
  // Format check on a typed override — shape depends on the contract's
  // chosen format. Empty value means the user accepted the auto-generated
  // default; skip the check.
  if (
    state.numberOverrideMode &&
    state.number.trim().length > 0
  ) {
    const trimmed = state.number.trim();
    if (numberFormat === "nn") {
      if (!/^\d+$/.test(trimmed)) {
        errors.number = "Number must be digits only (e.g. 01)";
      }
    } else if (!/^VAR-\d{4}-\d{3}$/.test(trimmed)) {
      errors.number = "Number must match VAR-YYYY-NNN";
    }
  }
  return errors;
}

function hasErrors(errors: VariationFieldErrors): boolean {
  return Object.values(errors).some((v) => v !== undefined);
}

const modeTitle: Record<VariationFormMode, string> = {
  create: "Add variation",
  edit: "Edit variation",
};

export default function VariationFormModal({
  isOpen,
  onClose,
  mode,
  contractId,
  source,
  gstApplies,
  sows,
  existingVariations = [],
  numberFormat = "nn",
  onCreate,
  onUpdate,
  validateUniqueNumber,
  isSubmitting,
}: VariationFormModalProps) {
  const [errors, setErrors] = useState<VariationFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Re-seed whenever the modal opens or its target changes. The seedKey
  // encodes mode + source id so that:
  //   - Re-opens for a different variation always seed fresh data.
  //   - A freshly-derived existingVariations array from the parent does not
  //     re-seed what the user has already typed (existingVariations is only
  //     read at seed time, not tracked as a dependency).
  // The modal is eager-mounted (only isOpen toggles) — useSeededFormState
  // resets its guard on close, so reopening with the same key (e.g.
  // create → create) re-seeds instead of showing the previous values.
  const [state, setState] = useSeededFormState<VariationFormState>(
    isOpen,
    `${mode}:${source?.id ?? "new"}`,
    () => {
      if (mode === "edit" && source) return stateFromVariation(source);
      const seeded = buildEmptyVariationFormState();
      seeded.number = nextTrackingNumber(
        "VAR",
        existingVariations,
        new Date(),
        numberFormat,
      );
      return seeded;
    },
    () => {
      setErrors({});
      setSubmitError(null);
    },
  );

  const handleChange = (patch: Partial<VariationFormState>) => {
    setState((prev) => ({ ...prev, ...patch }));
    const keys = Object.keys(patch);
    setErrors((prev) => {
      const next = { ...prev };
      for (const k of keys) {
        delete next[k as keyof VariationFieldErrors];
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(state, numberFormat);
    if (hasErrors(v)) {
      setErrors(v);
      return;
    }
    setSubmitError(null);
    try {
      if (mode === "edit" && source && onUpdate) {
        await onUpdate(
          toUpdateVariationInput(state, contractId, source.version),
        );
      } else if (mode === "create" && onCreate) {
        await onCreate(toCreateVariationInput(state, contractId));
      }
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to save variation.",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modeTitle[mode]}
      maxWidth="sm:max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <VariationFormFields
          value={state}
          onChange={handleChange}
          errors={errors}
          idPrefix={`variation-${mode}`}
          mode={mode}
          gstApplies={gstApplies}
          sows={sows}
          validateUniqueNumber={validateUniqueNumber}
          disabled={isSubmitting}
          sourceStatus={mode === "edit" ? source?.status : undefined}
        />
        {submitError && <Alert variant="error">{submitError}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            {mode === "edit" ? "Save changes" : "Create variation"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
